import { Button } from 'components/common/Button'
import { Info } from 'components/common/Info'
import { Separator } from 'components/common/Separator'
import { Paragraph, Title } from 'components/common/ways-to/Typography'
import { WaysToProps } from 'components/common/ways-to/WaysTo'
import { Currencies } from 'constants/currencies'
import { useCurrencies } from 'contexts'
import { useEditionPrices } from 'hooks/useEditionPrices'
import Trans from 'next-translate/Trans'
import useTranslation from 'next-translate/useTranslation'
import { BuyingTradeType } from 'types'
import { getArtworkUrlFromRoot } from 'utils/urls'

export function WaysToBuy({
  edition,
  artwork,
  artists,
}: Omit<WaysToProps, 'type'>) {
  const { t } = useTranslation('common')
  const { highestBid, lowestAsk } = useEditionPrices({ edition })
  const { formatCurrency } = useCurrencies()

  function getActionHref(action: BuyingTradeType) {
    return `${getArtworkUrlFromRoot(
      'bid-or-buy',
      artists,
      artwork?.name,
      artwork?.id
    )}/${edition?.name}?action=${action}`
  }

  return (
    <div className='space-y-6'>
      {!lowestAsk && (
        <>
          <div className='space-y-2.5'>
            <Title>{t('how_it_works_drawer.buying.no_listings.1.title')}</Title>
            <Info>
              <Trans
                i18nKey='common:how_it_works_drawer.buying.no_listings.1.info'
                components={{ b: <b /> }}
              />
            </Info>
            <Paragraph>
              {t('how_it_works_drawer.buying.no_listings.1.description')}
            </Paragraph>
          </div>

          <Separator color='cool-400' />

          <div className='space-y-2.5'>
            <Title>{t('how_it_works_drawer.buying.no_listings.2.title')}</Title>

            <div className='space-y-6'>
              <Paragraph>
                {t('how_it_works_drawer.buying.no_listings.2.description_1')}
              </Paragraph>
              <Paragraph>
                <Trans
                  i18nKey='common:how_it_works_drawer.buying.no_listings.2.description_2'
                  components={{ b: <b /> }}
                />
              </Paragraph>
              <Paragraph>
                {t('how_it_works_drawer.buying.no_listings.2.description_3')}
              </Paragraph>
            </div>

            <Button
              className='w-full'
              style='solid-success'
              pill
              type='link'
              href={getActionHref('bid')}
            >
              {t('how_it_works_drawer.buying.no_listings.2.action')}
            </Button>
          </div>
        </>
      )}

      {Boolean(lowestAsk) && (
        <>
          <div className='space-y-2.5'>
            <Title>
              {t('how_it_works_drawer.buying.with_listings.1.title')}
            </Title>
            <Paragraph>
              <Trans
                i18nKey='common:how_it_works_drawer.buying.with_listings.1.description'
                components={{ b: <b /> }}
                values={{
                  lowestListing: formatCurrency(lowestAsk, {
                    from: Currencies.USER,
                    to: Currencies.USER,
                  }),
                }}
              />
            </Paragraph>
            <Button
              className='w-full'
              style='solid-success'
              pill
              type='link'
              href={getActionHref('buy')}
            >
              {t('how_it_works_drawer.buying.with_listings.1.action')}
            </Button>
          </div>

          <Separator color='cool-400' />

          <div className='space-y-2.5'>
            <Title>
              {t('how_it_works_drawer.buying.with_listings.2.title')}
            </Title>

            <div className='space-y-6'>
              <Paragraph>
                {t('how_it_works_drawer.buying.with_listings.2.description_1')}
              </Paragraph>
              <Paragraph>
                <Trans
                  i18nKey={
                    highestBid
                      ? 'common:how_it_works_drawer.buying.with_listings.2.description_2_with_offer'
                      : 'common:how_it_works_drawer.buying.with_listings.2.description_2_without_offer'
                  }
                  components={{ b: <b /> }}
                  values={{
                    highestOffer: formatCurrency(highestBid, {
                      from: Currencies.USER,
                      to: Currencies.USER,
                    }),
                  }}
                />
              </Paragraph>
              <Paragraph>
                {t('how_it_works_drawer.buying.with_listings.2.description_3')}
              </Paragraph>
            </div>

            <Button
              className='w-full'
              style='outline'
              pill
              type='link'
              href={getActionHref('bid')}
            >
              {t('how_it_works_drawer.buying.with_listings.2.action')}
            </Button>
          </div>
        </>
      )}
    </div>
  )
}
